import React, {ChangeEvent, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import {ButtonVariant, IUserGroup} from '@wholesalechange/chatcomponent';
import {GritxButton} from '@wholesalechange/chatcomponent';
import {Input} from 'components/input/Input';
import {InputPhone} from 'components/input-phone/InputPhone';
import {isValidPhoneNumber} from 'react-phone-number-input';

import {IUserProfile} from '@wholesalechange/chatcomponent';
import {IStore} from 'redux/interface';
import {loadUserGroupsAction, logoutAction, updateUserProfileAction} from 'redux/auth/authAsyncActions';

import {isValidEmail} from 'utils/helpers/validators';
import {ILanguage, languages} from 'utils/constants/constants';

import {NavLink} from 'react-router-dom';
import {useChangeLanguage} from '../../utils/hooks/useChangeLanguage';
import InputCheckbox from '../../components/input-checkbox';
import {updateProfile} from '../../redux/pageAsyncActions';
import Loader from '../../components/loader';
import GritxSelect from '../../components/gritx-select';
import InputRadio from '../../components/input-radio';
import {IRadioInput} from '../../components/input-radio/interfaces';
import {IAdditionalField} from '../../redux/auth/interfaces';

type RequiredField = { required?: boolean };

function getIsSaveDisabled(params: {
  isChangeForm: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: string;
  age?: string;
  grade?: { id: number; name: string };
  school?: IUserGroup;
  genderField?: RequiredField;
  ageField?: RequiredField;
  gradeField?: RequiredField;
  schoolField?: RequiredField;
}) {
  const {
    isChangeForm,
    firstName,
    lastName,
    email,
    phone,
    gender,
    age,
    grade,
    school,
    genderField,
    ageField,
    gradeField,
    schoolField
  } = params;

  if (!isChangeForm) return true;

  const isRequiredFieldValid = (field?: RequiredField, value?: unknown) => {
    return !field || !field.required || Boolean(value);
  };

  const hasEmptyNames = firstName?.trim() === '' || lastName?.trim() === '';

  const hasInvalidContacts = !email
    || !isValidEmail(email)
    || (phone && !isValidPhoneNumber(phone));

  const hasInvalidAdditionalFields = !isRequiredFieldValid(genderField, gender)
    || !isRequiredFieldValid(ageField, age)
    || !isRequiredFieldValid(gradeField, grade)
    || !isRequiredFieldValid(schoolField, school);

  return hasEmptyNames || hasInvalidContacts || hasInvalidAdditionalFields;
}

const genderRadioInputs: IRadioInput[] = [
  {
    id: 0,
    name: 'Male',
    label: 'Male',
    value: 'Male'
  },
  {
    id: 1,
    name: 'Female',
    label: 'Female',
    value: 'Female'
  }];

const grades: {id: number, name: string}[] = Array.from({length: 12}, (_, i) => ({
  id: i,
  name: `K-${i + 1}` as const
}));

export const SettingsForm = (): JSX.Element => {
  const {userProfile, userGroups} = useSelector((state: IStore) => state.auth);
  const {settings} = useSelector((state: IStore) => state.auth);
  const {loading} = useSelector((state: IStore) => state.status);

  const dispatch = useDispatch();
  const intl = useIntl();

  const {
    changeLanguage
  } = useChangeLanguage();
  const [firstName, setFirstName] = useState(userProfile?.firstName);
  const [lastName, setLastName] = useState(userProfile?.lastName);
  const [email, setEmail] = useState(userProfile?.email);
  const [phone, setPhone] = useState(userProfile?.phone || '');
  const [isRequiredEmail, setIsRequiredEmail] = useState(false);
  const [isChangeForm, setIsChangeForm] = useState(false);
  const [allowedLanguages, setAllowedLanguages] = useState<ILanguage[]>([]);
  const [allowSms, setAllowSms] = useState(userProfile?.allowSms || false);
  const [gender, setGender] = useState(userProfile?.gender || '');
  const [age, setAge] = useState(userProfile?.age || '');
  const [grade, setGrade] = useState<{id: number, name: string}>();
  const [school, setSchool] = useState<IUserGroup>();
  const [allowSmsField, setAllowSmsField] = useState<IAdditionalField>();
  const [genderField, setGenderField] = useState<IAdditionalField>();
  const [ageField, setAgeField] = useState<IAdditionalField>();
  const [gradeField, setGradeField] = useState<IAdditionalField>();
  const [schoolField, setSchoolField] = useState<IAdditionalField>();

  useEffect(() => {
    if (userProfile?.email) {
      setIsRequiredEmail(true);
    }
  }, [userProfile?.email]);

  useEffect(() => {
    if (settings?.languages) {
      setAllowedLanguages(languages
        .filter(item => settings?.languages.includes(item.value))
      );
    }
  }, [settings?.languages]);

  useEffect(() => {
    if (settings?.profileSettings?.additionalFields) {
      setAllowSmsField(settings?.profileSettings?.additionalFields.find(f => f.name === 'allowSms'));
      setGenderField(settings?.profileSettings?.additionalFields.find(f => f.name === 'gender'));
      setAgeField(settings?.profileSettings?.additionalFields.find(f => f.name === 'age'));
      setGradeField(settings?.profileSettings?.additionalFields.find(f => f.name === 'grade'));

      const s = settings?.profileSettings?.additionalFields.find(f => f.name === 'school');

      if (s) {
        setSchoolField(s);
        dispatch(loadUserGroupsAction());
      }
    }
  }, [settings?.profileSettings?.additionalFields]);

  useEffect(() => {
    if (userProfile) {
      const g = grades.find(t => t.name.toString() === userProfile.grade);

      if (g) {
        setGrade(g);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile && userGroups && userGroups.length) {
      const s = userGroups.find(t => t.name.toString() === userProfile.school);

      if (s) {
        setSchool(s);
      }
    }
  }, [userProfile, userGroups]);

  function handleChangeFirstName(value: string) {
    setFirstName(value);
    setIsChangeForm(true);
  }

  function handleChangeLastName(value: string) {
    setLastName(value);
    setIsChangeForm(true);
  }

  function handleChangeEmail(value: string) {
    setEmail(value);
    setIsChangeForm(true);
  }

  function handleChangePhone(value: string) {
    const newPhone = value || '';

    setPhone(value);
    if (newPhone !== userProfile?.phone) {
      setIsChangeForm(true);
    }
  }

  function handleChangeAllowSms(value: boolean) {
    setAllowSms(value);
    setIsChangeForm(true);
  }

  function handleChangeGender(value: string) {
    setGender(value);
    setIsChangeForm(true);
  }

  function handleChangeAge(value: string) {
    setAge(value);
    setIsChangeForm(true);
  }

  function handleChangeSchool(e: ChangeEvent<HTMLSelectElement>) {
    const newSchool = userGroups.find(t => t.id.toString() === e.target.value);

    if (newSchool) {
      setSchool(newSchool);
      setIsChangeForm(true);
    }
  }

  function handleChangeGrade(e: ChangeEvent<HTMLSelectElement>) {
    const newGrade = grades.find(t => t.id.toString() === e.target.value);

    if (newGrade) {
      setGrade(newGrade);
      setIsChangeForm(true);
    }
  }

  const isSaveDisabled = getIsSaveDisabled({
    isChangeForm,
    firstName,
    lastName,
    email,
    phone,
    gender,
    age,
    grade,
    school,
    genderField,
    ageField,
    gradeField,
    schoolField
  });

  function handleChangeLang(lang: string) {
    return async () => {
      if (userProfile) {
        changeLanguage(userProfile, lang);
      }
    };
  }

  function handleSave() {
    setFirstName(firstName?.trim());
    setLastName(lastName?.trim());

    dispatch(updateUserProfileAction({
      ...userProfile,
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
      email: email,
      phone: phone || '',
      allowSms: allowSms,
      gender: gender,
      age: age,
      school: school?.name,
      grade: grade?.name
    } as IUserProfile));

    setIsChangeForm(false);
  }

  function handleSignOut() {
    dispatch(logoutAction());
  }

  function showLanguages() {
    return allowedLanguages && allowedLanguages.length > 1;
  }

  return (
    <>
      {loading.includes(updateProfile) && <Loader nested/>}
      <div className="settings__content">
        <div className="settings__info info">
          <div className="info__wrap">
            <div className="info__language">
              {
                showLanguages() && allowedLanguages.map((item, index) => {
                  return (
                    <GritxButton
                      key={index}
                      title={item.title}
                      variant={userProfile?.language === item.value
                        ? ButtonVariant.Primary
                        : ButtonVariant.Outline
                      }
                      onClick={handleChangeLang(item.value)}
                      className="info__language-btn"
                    />
                  );
                })
              }
            </div>
            <div className="info__content">
              <Input
                className="info__item"
                label={intl.formatMessage({
                  id: 'gritx.user-settings.firstName',
                  defaultMessage: 'First Name'
                })}
                required
                maxLength={100}
                type="text"
                placeholder={intl.formatMessage(({
                  id: 'gritx.user-settings.firstName.placeholder',
                  defaultMessage: 'Please, enter your First Name'
                }))}
                value={firstName}
                onChange={handleChangeFirstName}
              />
              <Input
                className="info__item"
                label={intl.formatMessage({
                  id: 'gritx.user-settings.lastName',
                  defaultMessage: 'Last Name'
                })}
                required
                maxLength={100}
                type="text"
                placeholder={intl.formatMessage(({
                  id: 'gritx.user-settings.lastName.placeholder',
                  defaultMessage: 'Please, enter your Last Name'
                }))}
                value={lastName}
                onChange={handleChangeLastName}
              />
              <Input
                className="info__item"
                label={intl.formatMessage({
                  id: 'gritx.user-settings.email',
                  defaultMessage: 'Email'
                })}
                required={isRequiredEmail}
                type="email"
                placeholder={intl.formatMessage(({
                  id: 'gritx.user-settings.email.placeholder',
                  defaultMessage: 'Please, enter your Email'
                }))}
                value={email}
                onChange={handleChangeEmail}
              />
              <InputPhone
                labelClassName="info__phone"
                className="info__phone"
                userPhoneNumber={phone}
                onChange={handleChangePhone}
              />

              {genderField && <InputRadio
                className="info__radio"
                inputs={genderRadioInputs}
                value={gender}
                onChange={handleChangeGender}
                label={intl.formatMessage({
                  id: 'gritx.user-settings.gender',
                  defaultMessage: 'Gender'
                })}
                required={genderField.required}
                labelOnNewLine/>}

              {ageField && <Input
                className="info__item"
                label={intl.formatMessage({
                  id: 'gritx.user-settings.age',
                  defaultMessage: 'Age'
                })}
                required={ageField.required}
                type="number"
                placeholder={intl.formatMessage(({
                  id: 'gritx.user-settings.age.placeholder',
                  defaultMessage: 'Please, enter your Age'
                }))}
                value={age}
                minValue={1}
                onChange={handleChangeAge}
              />}

              {gradeField && <GritxSelect
                name={'grade'}
                options={grades}
                placeholder={intl.formatMessage({
                  id: 'gritx.user-settings.grade.placeholder',
                  defaultMessage: 'Please, choose your grade'
                })}
                defaultValue={'placeholder'}
                value={grade?.id}
                onChange={handleChangeGrade}
                onBlur={handleChangeGrade}
                className="info__selector"
                selectClassName="info__select"
                label={'Grade'}
                required={gradeField.required}
              />}
              {schoolField && <GritxSelect
                name={'school'}
                options={userGroups}
                placeholder={intl.formatMessage({
                  id: 'gritx.user-settings.school.placeholder',
                  defaultMessage: 'Please, choose your school'
                })}
                defaultValue={'placeholder'}
                value={school?.id}
                onChange={handleChangeSchool}
                onBlur={handleChangeSchool}
                className="info__selector"
                selectClassName="info__select"
                label={'School'}
                required={schoolField.required}
              />}
              {allowSmsField && <InputCheckbox value={allowSms}
                onChange={handleChangeAllowSms}
                label={intl.formatMessage(({
                  id: 'gritx.user-settings.allowSms',
                  defaultMessage: 'Allow sending SMS'
                }))}
              />}
              <div className="settings__save">
                <GritxButton
                  className="settings__save-btn"
                  title={intl.formatMessage({
                    id: 'gritx.user-settings.buttonSave',
                    defaultMessage: 'Save'
                  })}
                  disabled={isSaveDisabled}
                  variant={ButtonVariant.Primary}
                  onClick={handleSave}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="settings__log-out">
          <NavLink
            to={'/logout'}
          >
            <GritxButton
              className="settings__log-out-btn"
              title={intl.formatMessage({
                id: 'gritx.user-settings.buttonLogOut',
                defaultMessage: 'Log Out'
              })}
              variant={ButtonVariant.Outline}
              onClick={handleSignOut}
            />
          </NavLink>
        </div>
      </div>
    </>
  );
};
